import { useEffect, useRef, useState } from 'react'

import { captureException } from '@sentry/react'
import { onSnapshot, type Query, type QuerySnapshot, type Unsubscribe } from 'firebase/firestore'
import { omit } from 'lodash-es'

export interface UseMultipleQueryDataOptions<T, D> {
  readonly collectionName: string
  readonly keys: string[]
  queryFn(id: string): Query<D>
  dataFn(snapshot: QuerySnapshot<D>): T | undefined
}

export type UseMultipleQueryDataResult<T> = Record<string, T>

export const useMultipleQueryData = <T, D>({
  keys,
  collectionName,
  queryFn,
  dataFn,
}: UseMultipleQueryDataOptions<T, D>): UseMultipleQueryDataResult<T> => {
  const snapshotUnsubscribes = useRef({} as Record<string, Unsubscribe>)
  const [state, setState] = useState<UseMultipleQueryDataResult<T>>({})
  const queryFnRef = useRef(queryFn)
  const dataFnRef = useRef(dataFn)

  useEffect(() => {
    const snapshotToRemove = Object.keys(snapshotUnsubscribes.current).filter((id) => !keys.includes(id))
    const snapshotToAdd = keys.filter((id) => !snapshotUnsubscribes.current[id])

    snapshotUnsubscribes.current = snapshotToRemove.reduce((acc, id) => {
      const { [id]: unsubscribe, ...rest } = acc
      unsubscribe()
      return rest
    }, snapshotUnsubscribes.current)

    snapshotUnsubscribes.current = snapshotToAdd.reduce((acc, id) => {
      const unsubscribe = onSnapshot(
        queryFnRef.current(id),
        (snapshot) => {
          setState((prevState) => {
            const value = dataFnRef.current(snapshot)
            return value === undefined ? omit(prevState, id) : { ...prevState, [id]: value }
          })
        },
        (error) => {
          captureException(error, {
            tags: { 'firestore.query.snapshot': collectionName },
            fingerprint: ['firestore', 'query', collectionName],
          })
        }
      )
      return { ...acc, [id]: unsubscribe }
    }, snapshotUnsubscribes.current)
  }, [collectionName, keys])
  return state
}
